import { useEffect, useState, useRef } from "react";
import { useSession } from "Context/userAuthContext";
import { useNavigate } from "react-router-dom";
import { joinEmailList, publishProfile } from "Util/firebaseHelper";
import _ from "lodash";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { reorder } from "Util/helpers";
import { BusinessInfo } from "Model/BusinessInfo";
import { readBusinessInfo } from "Util/firebaseHelper";
import Stories from "react-insta-stories";

// ui related
import {
  Button,
  Typography,
  Stack,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  TextField,
  Chip,
} from "@mui/material";
import { CardStyle } from "Components/AllComponents";
import { Profile } from "Model/Profile";
import { useAppDispatch } from "Redux/hooks";
import { editSellerProfile } from "Redux/actionCreators";
import { AvailabilityType } from "Model/Availability";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "Components/Styles/carousel.css";
import { PresaleEntry } from "Pages/CustomerView/CustomerPresale/Components/PresaleEntry";

// icons
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import { PaymentLinks } from "Pages/BusinessView/Hub/Home/Components/PaymentLinks";
import { toast } from "react-toastify";

// Hook to detect if text is truncated
const useIsTruncated = (text: string, isExpanded: boolean) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && !isExpanded) {
        const element = textRef.current;
        // Check if content would be truncated when collapsed
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [text, isExpanded]);

  return { isTruncated, textRef };
};

type HomeProps = {
  sellerProfile: Profile;
};

export const HomeContent = ({ sellerProfile }: HomeProps) => {
  const { user } = useSession();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const formPublished = sellerProfile.forms && sellerProfile.forms.length > 0;
  const [email, setEmail] = useState("");
  const [joinedEmailList, setJoinedEmailList] = useState(false);

  const [localLinks, setLocalLinks] = useState(sellerProfile.links);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const { isTruncated, textRef } = useIsTruncated(
    sellerProfile.sellerIntro || "",
    isBioExpanded
  );

  useEffect(() => {
    setLocalLinks(sellerProfile.links);
  }, [sellerProfile.links]);

  useEffect(() => {
    async function fetchData() {
      if (formPublished) {
        const form = await readBusinessInfo(sellerProfile.forms[0]);
        let content = form.data();
        if (content) {
          setBusinessInfo({
            id: content.id,
            businessName: content.businessName,
            outsideUS: content.outsideUS || false,
            formIntro: content.formIntro,
            sellerName: content.sellerName,
            sellerIntro: content.sellerIntro,
            sellerCity: content.sellerCity,
            sellerState: content.sellerState,
            fulfillmentMethod: content.fulfillmentMethod,
            shipOnlyToMyState: content.shipOnlyToMyState || false,
            minOrder: content.minOrder,
            priceChoice: content.priceChoice,
            price: content.price,
            sellerBookedUp: content.sellerBookedUp,
            sellerAvailability: content.sellerAvailability,
            orderDetails: content.orderDetails,
            sellerEmail: content.sellerEmail,
            bucketLists: content.bucketLists || [""],
            flavors: content.flavors || [""],
            packagings: content.packagings || [""],
            paymentTypes: content.paymentTypes || [""],
            agreements: content.agreements || "",
            showBucketLists: content.showBucketLists || false,
            showFlavors: content.showFlavors || false,
            showPackagings: content.showPackagings || false,
            showPaymentTypes: content.showPaymentTypes || false,
            sellerPhoto: content.sellerPhoto || "",
            aic: content.aic || false,
            username: content.username || "",
          });
        }
      }
    }
    fetchData();
  }, []);

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) {
      return;
    }
    const newLinks = reorder(localLinks, source.index, destination.index);
    setLocalLinks(newLinks);
    publishProfile({ ...sellerProfile, links: newLinks }, user?.uid || "");
    dispatch(
      editSellerProfile({
        ...sellerProfile,
        links: newLinks,
      })
    );
  };

  const toggleExpand = () => {
    setIsBioExpanded(!isBioExpanded);
  };

  const SellerAvatar = ({ photo }: { photo: string }) => (
    <Stack
      sx={{
        backgroundColor: "white",
        p: 0.25,
        borderRadius: "50%",
        alignItems: "center",
        textAlign: "center",
        width: "fit-content",
        zIndex: "1",
      }}
    >
      {photo === "" ? (
        <svg
          viewBox="0 0 36 36"
          fill="none"
          role="img"
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
        >
          <mask
            id=":r126:"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="36"
            height="36"
          >
            <rect width="36" height="36" rx="72" fill="#FFFFFF" />
          </mask>
          <g mask="url(#:r126:)">
            <rect width="36" height="36" fill="#c8c8b9" />
            <rect
              x="0"
              y="0"
              width="36"
              height="36"
              transform="translate(-2 6) rotate(306 18 18) scale(1)"
              fill="#eeda8d"
              rx="36"
            />
            <g transform="translate(-2 6) rotate(6 18 18)">
              <path d="M13,19 a1,0.75 0 0,0 10,0" fill="#000000" />
              <rect
                x="13"
                y="14"
                width="1.5"
                height="2"
                rx="1"
                stroke="none"
                fill="#000000"
              />
              <rect
                x="21"
                y="14"
                width="1.5"
                height="2"
                rx="1"
                stroke="none"
                fill="#000000"
              />
            </g>
          </g>
        </svg>
      ) : (
        <img
          src={photo}
          width={64}
          height={64}
          style={{ objectFit: "cover", borderRadius: "50%" }}
        />
      )}
    </Stack>
  );

  const SellerHeader = ({ sellerProfile }: { sellerProfile: any }) => (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Stack flex={1} spacing={0.5}>
        <Typography color="text.primary" variant="h5" flex={1}>
          {sellerProfile.businessName}
        </Typography>
        <Box sx={{ display: "block" }}>
          <Chip
            label={`${sellerProfile.sellerCity || "City"}, ${sellerProfile.sellerState || "State"}`}
            size="small"
            sx={{ mr: 1, mt: 0.5 }}
          />
          <Chip
            label={sellerProfile.fulfillmentMethod}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>
      </Stack>
      <SellerAvatar photo={sellerProfile.sellerPhoto} />
    </Stack>
  );

  const SellerBio = ({
    sellerProfile,
    isBioExpanded,
  }: {
    sellerProfile: any;
    isBioExpanded: boolean;
  }) => (
    <Stack>
      {sellerProfile.sellerName && (
        <Typography variant="subtitle2">
          Meet {sellerProfile.sellerName}
        </Typography>
      )}
      {sellerProfile.sellerIntro && (
        <Typography
          ref={textRef}
          variant="body1"
          sx={{
            color: "text.primary",
            whiteSpace: "pre-line",
            ...(!isBioExpanded &&
              sellerProfile.sellerPortrait && {
                display: "-webkit-box",
                WebkitLineClamp: { xs: 1, md: 2 },
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
          }}
        >
          {sellerProfile.sellerIntro}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* ------------- profile section ------------- */}
      {(sellerProfile.sellerName || sellerProfile.sellerIntro) &&
      sellerProfile.sellerPortrait ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "4/3",
            overflow: "hidden",
            borderRadius: 5,
          }}
        >
          {/* Background Image */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 5,
            }}
          >
            <img
              src={sellerProfile.sellerPortrait}
              alt={sellerProfile.sellerName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "all 0.1s ease",
                opacity: isBioExpanded ? 0.8 : 1,
                display: "block",
              }}
            />
          </Box>
          {/* Text Content Overlay */}
          <Stack
            spacing={1}
            sx={{
              cursor: isTruncated || isBioExpanded ? "pointer" : "default",
              maxHeight: "100%",
              overflowY: isBioExpanded ? "auto" : "visible",
              overflowX: "hidden",
              padding: { xs: 2, sm: 3 },
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
            onClick={(e) => {
              if (isTruncated || isBioExpanded) {
                e.stopPropagation();
                toggleExpand();
              }
            }}
          >
            <SellerHeader sellerProfile={sellerProfile} />
            <SellerBio
              sellerProfile={sellerProfile}
              isBioExpanded={isBioExpanded}
            />
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1}>
          <SellerHeader sellerProfile={sellerProfile} />
          <SellerBio sellerProfile={sellerProfile} isBioExpanded={false} />
        </Stack>
      )}

      {/* ------------- presale ------------- */}
      {sellerProfile.livePresales.map((p) => (
        <PresaleEntry
          editing={false}
          key={p}
          presaleId={p}
          username={sellerProfile.username}
        />
      ))}

      {/* ------------- order form ------------- */}
      {formPublished ? (
        <Stack
          onClick={() => {
            sellerProfile.availability.type ===
            AvailabilityType.doNotAcceptOrder
              ? console.log("not accepting order.")
              : navigate(
                  "/" +
                    sellerProfile.username +
                    "/form/" +
                    sellerProfile.forms[0]
                );
          }}
          sx={{
            backgroundColor: "white",
            borderRadius: 5,
            p: { xs: 2, sm: 3 },
            "&:hover": {
              cursor:
                sellerProfile.availability.type ===
                AvailabilityType.doNotAcceptOrder
                  ? "default"
                  : "pointer",
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={{ sm: 1, xs: 2 }}>
            <Stack flex={1}>
              <Typography
                variant="h6"
                flex={1}
                sx={{ fontFamily: "Lora", fontSize: "1.3rem" }}
              >
                Custom order
              </Typography>
              {sellerProfile.availability.type ===
              AvailabilityType.doNotAcceptOrder ? (
                <Typography flex={1} variant="body1" color="text.secondary">
                  Closed for new orders
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Starts at ${businessInfo?.price}/dozen •{" "}
                  {businessInfo?.minOrder} dozen minimum
                </Typography>
              )}
            </Stack>
            {sellerProfile.availability.type !==
              AvailabilityType.doNotAcceptOrder &&
            !sellerProfile.hideImageGallery &&
            sellerProfile.imageGallery.length > 0 ? null : (
              <Button variant="contained">Get a quote</Button>
            )}
          </Stack>
          {sellerProfile.availability.type !==
            AvailabilityType.doNotAcceptOrder &&
          !sellerProfile.hideImageGallery &&
          sellerProfile.imageGallery.length > 0 ? (
            <Stack sx={{ marginTop: 2 }}>
              <Stories
                stories={sellerProfile.imageGallery.map((url) => ({ url }))}
                defaultInterval={2000}
                preventDefault
                width="100%"
                height="100%"
                loop
                storyStyles={{ width: "100%", overflow: "hidden" }}
                storyContainerStyles={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  aspectRatio: "1/1",
                }}
                storyInnerContainerStyles={{
                  backgroundColor: "white",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
                progressContainerStyles={{
                  filter: "none",
                  width: "200px",
                  bottom: 0,
                }}
              />
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <Button variant="contained">Request a quote</Button>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      {/* ------------- email list ------------- */}
      {sellerProfile.disableCustomerEmailList ? null : (
        <CardStyle>
          <Stack>
            <Typography
              variant="h6"
              flex={1}
              sx={{ fontFamily: "Lora", fontSize: "1.3rem" }}
            >
              Never miss a presale
            </Typography>
            <Typography variant="body1">
              Join the email list to be the first to know when a new presale
              launches.
            </Typography>
            {!joinedEmailList ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const result = await joinEmailList(email, sellerProfile.uid);
                  if (result) {
                    setJoinedEmailList(true);
                  } else {
                    toast.error(`Failed to add ${email}`, {
                      position: "top-center",
                      autoClose: 1600,
                    });
                  }
                }}
              >
                <Stack flexDirection="row" alignItems="center" mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    required={true}
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        height: "37.93px",
                      },
                      "& .MuiOutlinedInput-root": {
                        fieldset: { borderRightWidth: 0 },
                        "&.Mui-focused fieldset": {
                          borderWidth: "1px",
                          borderRightWidth: 0,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{
                      width: "160px",
                      height: "37.93px",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderTopRightRadius: 8,
                      borderBottomRightRadius: 8,
                      px: 0,
                    }}
                  >
                    Join email list
                  </Button>
                </Stack>
              </form>
            ) : (
              <Typography color="text.secondary">
                Thank you for joining!
              </Typography>
            )}
          </Stack>
        </CardStyle>
      )}

      {/* ------------- payment links ------------- */}
      {sellerProfile.payments.length > 0 ? (
        <PaymentLinks sellerProfile={sellerProfile} />
      ) : null}

      {/* ------------- custom links ------------- */}
      <List disablePadding>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable-list">
            {(provided) => (
              <Stack
                ref={provided.innerRef}
                {...provided.droppableProps}
                divider={<Divider orientation="horizontal" flexItem />}
              >
                {localLinks.map((link) => (
                  <ListItemButton href={link.url} target="_blank">
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1">
                          {link.title}
                        </Typography>
                      }
                      sx={{ color: "text.primary" }}
                    />
                    <CallMadeOutlinedIcon
                      sx={{ ml: 2, color: "text.primary" }}
                    />
                  </ListItemButton>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </List>
    </Stack>
  );
};
